import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AccessRequest } from "src/app/core/models/access-request.model";
import { UserStore } from "src/app/core/stores/user.store";

const LOG_TAG = "[ACCESS-REQUEST-DIALOG] ";
@Component({
  selector: "hydrao-access-request-dialog",
  templateUrl: "./access-request-dialog.component.html",
  styleUrls: ["./access-request-dialog.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, CommonModule, MatDialogModule],
})
export class AccessRequestDialogComponent {
  protected userStore: UserStore = inject(UserStore);
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private dialogRef: MatDialogRef<AccessRequestDialogComponent> = inject(
    MatDialogRef<AccessRequestDialogComponent>
  );

  request = inject(MAT_DIALOG_DATA) as AccessRequest;

  statusType = signal<string>("info");
  statusMessage = signal<string>(null);

  // ----- LIFE CYCLE METHODS -----

  public constructor() {
    effect(
      () => {
        const acceptResult = this.userStore.acceptResult$();

        if (!acceptResult) return;

        console.log(
          LOG_TAG + "EFFECT => show error or close dialog on success",
          acceptResult
        );

        if (
          typeof acceptResult === "string" &&
          acceptResult.trim().length > 0
        ) {
          console.log("ERROR MESSAGE", acceptResult);
          let message;
          switch (acceptResult) {
            case "UnexpectedError":
            case "unknown":
              message = $localize`:@@global.error.unexpectedError:Unexpected error, please try again`;
              break;
            default:
              console.error(`NO TRANSLATION FOR '${acceptResult}'`);
              message = acceptResult;
              break;
          }
          if (message) {
            this._setStatus("error", message);
          }
        } else {
          this.userStore.declareAcceptSuccess();
          this.close(true);
          this.snackBar.open(
            $localize`:@@user.accessRequestDialog.allowToast:Access to user ${this.request.user.email}:email: accepted successfully`,
            null,
            {
              duration: 3 * 1000,
            }
          );
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const denyResult = this.userStore.denyResult$();

        if (!denyResult) return;

        console.log(
          LOG_TAG + "EFFECT => show error or close dialog on success",
          denyResult
        );

        if (typeof denyResult === "string" && denyResult.trim().length > 0) {
          console.log("ERROR MESSAGE", denyResult);
          let message;
          switch (denyResult) {
            case "UnexpectedError":
            case "unknown":
              message = $localize`:@@global.error.unexpectedError:Unexpected error, please try again`;
              break;
            default:
              console.error(`NO TRANSLATION FOR '${denyResult}'`);
              message = denyResult;
              break;
          }
          if (message) {
            this._setStatus("error", message);
          }
        } else {
          this.userStore.declareDenySuccess();
          this.close(true);
          this.snackBar.open(
            $localize`:@@user.accessRequestDialog.denyToast:Access to user ${this.request.user.email}:email: denied successfully`,
            null,
            {
              duration: 3 * 1000,
            }
          );
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.userStore.resetAcceptResult();
    this.userStore.resetDenyResult();
  }

  // ----- USER INTERACTION METHODS ------

  public denyRequest(): void {
    console.log(LOG_TAG + "deny access request for " + this.request.user.email);

    this._setStatus("loading", $localize`:@@global.form.processing:processing` + "...");
    this.userStore.denyAccessRequest(this.request.user.id);
  }

  public allowRequest(): void {
    console.log(
      LOG_TAG + "accept access request for " + this.request.user.email
    );

    this._setStatus("loading", $localize`:@@global.form.processing:processing` + "...");
    this.userStore.acceptAccessRequest(this.request.user.id);
  }

  close(result: boolean = false) {
    this.dialogRef.close(result);
  }

  // ----- PRIVATE METHODS ------

  private _setStatus(type: string, message: string): void {
    this.statusType.set(type);
    this.statusMessage.set(message);
  }
}
